// A recipient picker: pick existing users (by name or email) from the directory and/or type any
// additional address. Used by the Notifications admin to build a notification's "also email" and
// "exclude" lists without hand-typing everyone. Values are stored as plain strings (an email, or —
// when `allowFragments` — a name fragment like "katsaras"); a value that matches a directory user is
// shown as their name.

import { useId, useMemo, useRef, useState } from 'react';
import { useIsCoarse } from '../lib/media.js';
import { fold, hasText } from '../lib/search.js';

export interface DirEntry {
  email: string;
  displayName: string;
}

export function RecipientPicker({
  value,
  onChange,
  users,
  allowFragments,
  placeholder,
  ariaLabel,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  users: DirEntry[];
  /** excludes accept a name fragment, not just a full address — relaxes the "looks like an email" hint. */
  allowFragments?: boolean;
  placeholder?: string;
  ariaLabel?: string;
}) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const listId = useId();
  // keyboard-highlighted option (aria-activedescendant); -1 = none. focus stays on the input.
  const [hi, setHi] = useState(-1);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // touch: the suggestions open under the field, where the on-screen keyboard is about to land
  const coarse = useIsCoarse();

  const selected = useMemo(() => new Set(value.map((v) => v.toLowerCase())), [value]);
  const nameOf = (email: string) => users.find((u) => u.email.toLowerCase() === email.toLowerCase())?.displayName;

  const matches = useMemo(() => {
    const needle = fold(q.trim());
    return users
      .filter((u) => !selected.has(u.email.toLowerCase()))
      .filter((u) => !needle || hasText(needle, u.email, u.displayName))
      .sort((a, b) => a.displayName.localeCompare(b.displayName))
      .slice(0, 8);
  }, [q, users, selected]);

  const add = (raw: string) => {
    const t = raw.trim();
    if (!t) return;
    if (!selected.has(t.toLowerCase())) onChange([...value, t]);
    setQ('');
    setHi(-1);
  };
  const remove = (v: string) => onChange(value.filter((x) => x !== v));

  const typed = q.trim();
  const typedIsNew = !!typed && !selected.has(typed.toLowerCase()) && !matches.some((u) => u.email.toLowerCase() === typed.toLowerCase());
  const menuOpen = open && (matches.length > 0 || typedIsNew);
  // options are the matches, then the "Add …" row as the last index.
  const optCount = matches.length + (typedIsNew ? 1 : 0);
  const pick = (i: number) => add(i < matches.length ? matches[i]!.email : typed);

  return (
    <div className="rcp">
      {value.length > 0 && (
        <div className="rcp-chips">
          {value.map((v) => {
            const nm = nameOf(v);
            return (
              <span key={v} className={`rcp-chip${nm ? '' : ' raw'}`} title={v}>
                <span className="rcp-chip-label">{nm ?? v}</span>
                {nm && <span className="rcp-chip-sub">{v}</span>}
                <button type="button" className="rcp-chip-x" aria-label={`Remove ${nm ?? v}`} onClick={() => remove(v)}>×</button>
              </span>
            );
          })}
        </div>
      )}
      <div className="rcp-field">
        <input
          className="field"
          style={{ width: '100%' }}
          value={q}
          placeholder={placeholder ?? 'Search people, or type an email…'}
          aria-label={ariaLabel}
          role="combobox"
          aria-expanded={menuOpen}
          aria-controls={menuOpen ? listId : undefined}
          aria-autocomplete="list"
          aria-activedescendant={menuOpen && hi >= 0 && hi < optCount ? `${listId}-${hi}` : undefined}
          onFocus={(e) => {
            if (blurTimer.current) clearTimeout(blurTimer.current);
            setOpen(true);
            // lift the field to the top of the page so the menu has the room above the keyboard. Instant:
            // a smooth scroll would race the browser's own keyboard scroll and stop part-way
            if (coarse) e.currentTarget.scrollIntoView({ block: 'start', behavior: 'instant' });
          }}
          onBlur={() => { blurTimer.current = setTimeout(() => { setOpen(false); setHi(-1); }, 120); }}
          onChange={(e) => { setQ(e.target.value); setOpen(true); setHi(-1); }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHi((h) => (optCount ? (h + 1) % optCount : -1)); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => (optCount ? (h <= 0 ? optCount - 1 : h - 1) : -1)); }
            else if (e.key === 'Enter') {
              e.preventDefault();
              if (menuOpen && hi >= 0 && hi < optCount) pick(hi);
              else if (typed) add(typed);
            }
            else if (e.key === 'Escape') { if (menuOpen) { e.preventDefault(); setOpen(false); setHi(-1); } }
            else if (e.key === 'Backspace' && !q && value.length) remove(value[value.length - 1]!);
          }}
        />
        {menuOpen && (
          <div className="rcp-menu" role="listbox" id={listId}>
            {matches.map((u, i) => (
              <button
                key={u.email}
                type="button"
                role="option"
                id={`${listId}-${i}`}
                aria-selected={i === hi}
                tabIndex={-1}
                className={`rcp-opt${i === hi ? ' on' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); add(u.email); }}
              >
                <span className="rcp-opt-name">{u.displayName}</span>
                <span className="rcp-opt-email">{u.email}</span>
              </button>
            ))}
            {typedIsNew && (
              <button
                type="button"
                role="option"
                id={`${listId}-${matches.length}`}
                aria-selected={hi === matches.length}
                tabIndex={-1}
                className={`rcp-opt rcp-opt-add${hi === matches.length ? ' on' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); add(typed); }}
              >
                Add “{typed}” <span className="muted">{allowFragments ? '- address or name fragment' : '- extra address'}</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
